import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChurchEventDetail, EventOptions } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { formatWhen, PeoplePicker, publicEventUrl, StatusBadge } from './shared';
import { ConflictList } from './Conflicts';

type Run = (action: () => Promise<unknown>) => Promise<void>;

/** Detalle de un evento: aprobación, invitaciones, checklist, enlace de invitación y confirmaciones. */
export function EventPage() {
  const { eventId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/calendar/churches/${churchId}/church-events/${eventId}`;
  const data = useAsync(
    async (signal) => {
      if (!churchId) return null;
      const event = await api.get<ChurchEventDetail>(base, signal);
      const options = event.canManage ? await api.get<EventOptions>(`/calendar/churches/${churchId}/church-events/options`, signal) : null;
      return { event, options };
    },
    [churchId, eventId],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run: Run = async (action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      data.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;

  return (
    <div className="page">
      <p className="small">
        <Link to="/app/eventos">← Eventos</Link>
      </p>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data ? (
        <EventView event={data.data.event} options={data.data.options} base={base} run={run} busy={busy} error={error} />
      ) : null}
    </div>
  );
}

function EventView({ event: e, options, base, run, busy, error }: { event: ChurchEventDetail; options: EventOptions | null; base: string; run: Run; busy: boolean; error: string | null }) {
  const [note, setNote] = useState('');
  const [canceling, setCanceling] = useState(false);
  return (
    <>
      <header className="page__header page__header--actions">
        <div>
          <h1>{e.title}</h1>
          <p className="muted">
            {e.ministry ? `Ministerio ${e.ministry.name}` : 'Evento general'} · <StatusBadge status={e.status} />{e.isPublic ? <> <span className="badge">Pública</span></> : null}
          </p>
        </div>
        {e.canManage && (e.status === 'approved' || e.status === 'pending') ? (
          <Link className="btn btn--secondary" to={`/app/eventos/${e.id}/editar`}>
            Editar
          </Link>
        ) : null}
      </header>
      {error ? <Alert>{error}</Alert> : null}
      {e.conflicts && e.conflicts.length > 0 ? <ConflictList items={e.conflicts} /> : null}

      {e.status === 'pending' ? (
        <Card title={e.canDecide ? 'Este evento espera tu aprobación' : 'Esperando aprobación'}>
          {e.canDecide ? (
            <>
              <div className="field">
                <label htmlFor="ev-note">Nota (opcional)</label>
                <input id="ev-note" value={note} maxLength={500} onChange={(x) => setNote(x.target.value)} />
              </div>
              <div className="actions">
                <Button busy={busy} onClick={() => void run(() => api.post(`${base}/decision`, { approve: true, note: note || null }))}>
                  Aprobar
                </Button>
                <Button variant="danger" busy={busy} onClick={() => { if (window.confirm('¿No aprobar este evento? Se le avisará a quien lo propuso.')) void run(() => api.post(`${base}/decision`, { approve: false, note: note || null })); }}>
                  No aprobar
                </Button>
              </div>
            </>
          ) : (
            <p className="muted">
              {e.scope === 'general' ? 'Lo aprueba el pastor o un diácono.' : `Lo autoriza el líder de ${e.ministry?.name}.`} Mientras tanto solo lo ven los líderes.
            </p>
          )}
        </Card>
      ) : null}
      {e.decision && e.status !== 'pending' ? (
        <Alert tone={e.status === 'rejected' ? 'error' : 'info'}>
          {e.status === 'rejected' ? 'No aprobado' : 'Aprobado'}
          {e.decision.by ? ` por ${e.decision.by.name}` : ''}
          {e.decision.note ? `: «${e.decision.note}»` : ''}
        </Alert>
      ) : null}
      {e.pendingInvitations.map((m) => (
        <Card key={m.id} title={`Invitan a ${m.name}`}>
          <p className="muted small">Como líder de {m.name}, decide si tu ministerio participa.</p>
          <div className="actions">
            <Button busy={busy} onClick={() => void run(() => api.post(`${base}/ministries/${m.id}`, { accept: true }))}>
              Aceptar
            </Button>
            <Button variant="secondary" busy={busy} onClick={() => void run(() => api.post(`${base}/ministries/${m.id}`, { accept: false }))}>
              Declinar
            </Button>
          </div>
        </Card>
      ))}

      <Card title="Detalles">
        <dl className="details">
          <dt>Cuándo</dt>
          <dd>{formatWhen(e.startsAt, e.endsAt)}</dd>
          {e.placeName || e.address ? (
            <>
              <dt>Dónde</dt>
              <dd>
                {e.placeName ? <strong>{e.placeName}</strong> : null}
                {e.address ? <span className="block">{e.address}</span> : null}
                {e.mapsUrl ? (
                  <a href={e.mapsUrl} target="_blank" rel="noopener noreferrer" className="block">
                    Ver en el mapa
                  </a>
                ) : null}
              </dd>
            </>
          ) : null}
          {e.purpose ? (
            <>
              <dt>Propósito</dt>
              <dd className="pre-line">{e.purpose}</dd>
            </>
          ) : null}
          <dt>Organiza</dt>
          <dd>{e.createdBy.name}</dd>
          {e.invitedMinistries.length > 0 ? (
            <>
              <dt>Ministerios</dt>
              <dd>
                <ul className="ev-chips">
                  {e.invitedMinistries.map((m) => (
                    <li key={m.id} className={`ev-chip ev-chip--${m.status}`}>
                      {m.name}
                      <span className="small"> · {m.status === 'accepted' ? 'participa' : m.status === 'pending' ? 'invitado' : 'declinó'}</span>
                    </li>
                  ))}
                </ul>
              </dd>
            </>
          ) : null}
          {e.people.length > 0 ? (
            <>
              <dt>Personas</dt>
              <dd>{e.people.map((p) => p.name).join(', ')}</dd>
            </>
          ) : null}
        </dl>
      </Card>

      <Checklist event={e} options={options} base={base} run={run} busy={busy} />

      {e.publicToken ? <InviteLink event={e} base={base} run={run} busy={busy} /> : null}

      {e.rsvps ? (
        <Card title={`Confirmaron asistencia (${e.rsvps.guests} ${e.rsvps.guests === 1 ? 'persona' : 'personas'})`}>
          {e.rsvps.items.length === 0 ? (
            <p className="muted small">Aún nadie confirma. Comparte el enlace de invitación.</p>
          ) : (
            <ul className="list">
              {e.rsvps.items.map((r, i) => (
                <li key={i} className="list__item">
                  <span className="agenda__body">
                    <strong>{r.name}</strong>
                    <span className="muted small">
                      {r.guests > 1 ? `${r.guests} personas` : '1 persona'}
                      {r.contact ? ` · ${r.contact}` : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {e.canCancel ? (
        canceling ? (
          <CancelForm onCancel={() => setCanceling(false)} onSubmit={(reason) => run(() => api.post(`${base}/cancel`, { reason }))} busy={busy} />
        ) : (
          <p>
            <Button variant="ghost" onClick={() => setCanceling(true)}>
              Cancelar evento
            </Button>
          </p>
        )
      ) : null}
    </>
  );
}

function CancelForm({ onSubmit, onCancel, busy }: { onSubmit: (reason: string) => Promise<void>; onCancel: () => void; busy: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <Card title="Cancelar evento">
      <form
        className="form"
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          void onSubmit(reason);
        }}
      >
        <div className="field">
          <label htmlFor="ev-cancel">Motivo</label>
          <input id="ev-cancel" required minLength={3} maxLength={500} value={reason} onChange={(x) => setReason(x.target.value)} />
        </div>
        <div className="actions">
          <Button type="submit" variant="danger" busy={busy} disabled={reason.trim().length < 3}>
            Cancelar evento
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Volver
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Checklist({ event: e, options, base, run, busy }: { event: ChurchEventDetail; options: EventOptions | null; base: string; run: Run; busy: boolean }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [people, setPeople] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const manage = e.canManage && e.status !== 'canceled' && e.status !== 'rejected';
  const done = e.tasks.filter((t) => t.done).length;
  if (e.tasks.length === 0 && !manage) return null;
  const toggleSel = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Card title={`Checklist${e.tasks.length ? ` (${done}/${e.tasks.length})` : ''}`}>
      {e.tasks.length === 0 ? <p className="muted small">Aún no hay tareas.</p> : null}
      <ul className="checklist">
        {e.tasks.map((t) => (
          <li key={t.id} className={`checklist__item${t.done ? ' checklist__item--done' : ''}`}>
            {manage ? (
              <input type="checkbox" className="checklist__select" aria-label={`Seleccionar ${t.title}`} checked={selected.includes(t.id)} onChange={() => toggleSel(t.id)} />
            ) : null}
            <div className="checklist__body">
              <label className="check">
                <input
                  type="checkbox"
                  checked={t.done}
                  disabled={!t.canToggle || busy}
                  aria-label={`${t.title} lista`}
                  onChange={() => void run(() => api.put(`${base}/tasks/${t.id}/done`, { done: !t.done }))}
                />
                <span className="checklist__title">{t.title}</span>
              </label>
              <span className="ev-chips">
                {t.assignees.length === 0 ? <span className="muted small">Sin asignar</span> : null}
                {t.assignees.map((a) => (
                  <span key={a.personId} className="ev-chip">
                    {a.name}
                    {manage ? (
                      <button
                        type="button"
                        className="ev-chip__x"
                        aria-label={`Quitar a ${a.name} de ${t.title}`}
                        onClick={() => void run(() => api.delete(`${base}/tasks/${t.id}/assignees/${a.personId}`))}
                      >
                        ×
                      </button>
                    ) : null}
                  </span>
                ))}
                {t.done && t.doneBy ? <span className="muted small">Hecho por {t.doneBy.name}</span> : null}
              </span>
            </div>
            {manage ? (
              <Button variant="ghost" aria-label={`Borrar ${t.title}`} onClick={() => void run(() => api.delete(`${base}/tasks/${t.id}`))}>
                Borrar
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      {manage ? (
        <>
          <form
            className="inline-form"
            onSubmit={(ev) => {
              ev.preventDefault();
              const titles = draft.split('\n').map((l) => l.trim()).filter(Boolean);
              if (titles.length) void run(() => api.post(`${base}/tasks`, { titles, assigneeIds: [] })).then(() => setDraft(''));
            }}
          >
            <label className="visually-hidden" htmlFor="ev-newtask">
              Nueva tarea
            </label>
            <input id="ev-newtask" placeholder="Nueva tarea…" value={draft} maxLength={200} onChange={(x) => setDraft(x.target.value)} />
            <Button type="submit" variant="secondary" busy={busy}>
              Agregar
            </Button>
          </form>
          {e.tasks.length > 0 && options ? (
            <div className="batch">
              <h3>Asignar</h3>
              <p className="muted small">
                Marca una o varias tareas (casilla de la izquierda) y elige a quién. {selected.length > 0 ? `${selected.length} seleccionadas.` : ''}
              </p>
              <div className="actions">
                <Button variant="ghost" onClick={() => setSelected(selected.length === e.tasks.length ? [] : e.tasks.map((t) => t.id))}>
                  {selected.length === e.tasks.length ? 'Quitar selección' : 'Seleccionar todas'}
                </Button>
              </div>
              <PeoplePicker label="Asignar a" idPrefix="asg" people={options.people} value={people} onChange={setPeople} />
              <Button
                disabled={selected.length === 0 || people.length === 0}
                busy={busy}
                onClick={() =>
                  void run(() => api.post(`${base}/assignments`, { taskIds: selected, personIds: people })).then(() => {
                    setSelected([]);
                    setPeople([]);
                  })
                }
              >
                Asignar {selected.length > 1 ? `${selected.length} tareas` : 'tarea'}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

function InviteLink({ event: e, base, run, busy }: { event: ChurchEventDetail; base: string; run: Run; busy: boolean }) {
  const url = publicEventUrl(e.publicToken!);
  const [copied, setCopied] = useState(false);
  const text = `Te invitamos a «${e.title}»`;
  return (
    <Card title="Invitación">
      <p className="muted small">Cualquier persona con este enlace ve el evento y puede confirmar su asistencia, aunque no tenga cuenta.</p>
      <div className="inline-form">
        <label className="visually-hidden" htmlFor="ev-link">
          Enlace de invitación
        </label>
        <input id="ev-link" readOnly value={url} onFocus={(x) => x.target.select()} />
        <Button
          variant="secondary"
          onClick={() =>
            void navigator.clipboard?.writeText(url).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            })
          }
        >
          {copied ? '¡Copiado!' : 'Copiar'}
        </Button>
      </div>
      <div className="actions">
        <a className="btn btn--primary" href={`https://wa.me/?text=${encodeURIComponent(`${text}: ${url}`)}`} target="_blank" rel="noopener noreferrer">
          Enviar por WhatsApp
        </a>
        {typeof navigator.share === 'function' ? (
          <Button variant="secondary" onClick={() => void navigator.share({ title: e.title, text, url }).catch(() => undefined)}>
            Compartir
          </Button>
        ) : null}
        {e.canManage ? (
          <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('El enlace anterior dejará de funcionar. ¿Cambiarlo?')) void run(() => api.post(`${base}/link`, {})); }}>
            Cambiar enlace
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
